/**
 * Postal code extraction from Google Maps results.
 *
 * Two Google APIs describe an address in two different shapes: the geocoder returns
 * `short_name`/`types` and the newer Places `Place` returns `shortText`/`types`.  Both are
 * read here so callers never have to care which one produced the result.
 *
 * Where no components are available at all — a typed search resolved only to a formatted
 * address string — `extractPostalCode()` recovers the code from the text.
 */

/** The overlap of `google.maps.GeocoderAddressComponent` and `google.maps.places.AddressComponent`. */
interface AddressComponentLike {
	short_name?: string | null
	shortText?: string | null
	types?: string[]
}

/** Geographic detail the location singleton accepts alongside a zip. */
export interface AddressGeoDetail {
	postalCode?: string
	country?: string
	region?: string
}

/**
 * Canadian codes are matched first: their letter/digit alternation cannot be mistaken for
 * anything else in an address, while a bare run of five digits can.
 */
const CA_POSTAL_PATTERN = /\b([A-Za-z]\d[A-Za-z])[ -]?(\d[A-Za-z]\d)\b/
const US_ZIP_PATTERN = /\b(\d{5})(?:-\d{4})?\b/g

/**
 * A leading five-digit run followed by more of the same address segment is the house number
 * of a street address.  A postal code in that position closes its segment instead, ending the
 * string or running straight into a comma.
 */
function isHouseNumber(match: RegExpMatchArray, formattedAddress: string): boolean {
	if (match.index !== 0) {
		return false
	}

	return !/^\s*(,|$)/.test(formattedAddress.slice(match[0].length))
}

function getComponentValue(components: AddressComponentLike[], type: string): string | undefined {
	const match = components.find(component => component?.types?.includes(type))

	return match?.short_name || match?.shortText || undefined
}

/** Google hands back `null` rather than an empty list when a result carries no components. */
function toComponentList(components?: AddressComponentLike[] | null): AddressComponentLike[] {
	return Array.isArray(components) ? components : []
}

/**
 * Read the postal code and the surrounding country/region out of an address component list.
 * Keys absent from the result rather than present and empty, so the whole object can be spread
 * onto a location write without contributing blanks.
 *
 * @param components - Address components from either the geocoder or a Places `Place`.
 */
export function getGeoDetailFromComponents(components?: AddressComponentLike[] | null): AddressGeoDetail {
	const componentList = toComponentList(components)
	const postalCode = getComponentValue(componentList, 'postal_code')
	const country = getComponentValue(componentList, 'country')
	const region = getComponentValue(componentList, 'administrative_area_level_1')

	return {
		...(postalCode ? { postalCode } : {}),
		...(country ? { country } : {}),
		...(region ? { region } : {}),
	}
}

/**
 * Recover a postal code from a formatted address.  US zips are taken from the last match in
 * the string, since a street number is five digits often enough ('12345 Main St') to make the
 * first match the wrong one, and Google places the postal code near the end.
 *
 * A five-digit run that opens the address and runs on into a street name is a house number,
 * not a zip — Google omits the postal code from some results, and '12345 Woodward Ave,
 * Detroit, MI, USA' would otherwise hand back a house number as the visitor's location.
 * Storing nothing beats storing a zip hundreds of miles from where they actually are.  A
 * leading zip that ends the segment ('48226', '48226, USA') is still read.
 *
 * Canadian codes are returned in canonical `A1A 1A1` form so the same address never persists
 * under two spellings.  US ZIP+4 collapses to its five-digit base, matching what the location
 * singleton stores.
 *
 * @param formattedAddress - Formatted address resolved by Google.
 */
export function extractPostalCode(formattedAddress?: string): string | undefined {
	if (!formattedAddress) {
		return undefined
	}

	const caMatch = formattedAddress.match(CA_POSTAL_PATTERN)

	if (caMatch) {
		return `${caMatch[1]} ${caMatch[2]}`.toUpperCase()
	}

	const usMatches = [...formattedAddress.matchAll(US_ZIP_PATTERN)].filter(match => !isHouseNumber(match, formattedAddress))
	const lastMatch = usMatches[usMatches.length - 1]

	return lastMatch ? lastMatch[1] : undefined
}
